import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type * as Types from '../../../graphql/types';
import { useGetHotelTeamForRecipientsQuery } from '../gql/_gen_/report-subscriptions.gql';
import {
  DELIVERY_TIME_OPTIONS,
  PRIORITY_OPTIONS,
  TIMEZONE_OPTIONS,
} from '../hooks/use-subscription-management';
import { clearFieldError, preferencesToInput, validateSubscriptionForm } from '../utils';
import { HotelTeamRecipientsSelect } from './hotel-team-recipients-select';
import { RmUserSelect } from './rm-user-select';

interface ReportSubscriptionPreferencesModalProps {
  subscription: Types.ReportSubscription;
  open: boolean;
  onClose: () => void;
  onSave: (id: string, input: Types.UpdateReportSubscriptionInput) => Promise<void>;
  loading?: boolean;
}

export const ReportSubscriptionPreferencesModal: React.FC<
  ReportSubscriptionPreferencesModalProps
> = ({ subscription, open, onClose, onSave, loading = false }) => {
  // Form state
  const [rmUserId, setRmUserId] = useState<number>(subscription.rmUserId);
  const [timezone, setTimezone] = useState(subscription.timezone);
  const [preferences, setPreferences] = useState<Types.ReportSubscriptionPreferences>(
    subscription.preferences,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Query hotel team to validate recipients exist
  const { data: hotelTeamData, loading: hotelTeamLoading } = useGetHotelTeamForRecipientsQuery({
    variables: { brandCode: subscription.brandCode },
  });
  const hotelTeamMembers = hotelTeamData?.getHotelTeam ?? [];
  const hasNoActiveTeamMembers = !hotelTeamLoading && hotelTeamMembers.length === 0;

  // Reset form when subscription changes
  useEffect(() => {
    setRmUserId(subscription.rmUserId);
    setTimezone(subscription.timezone);
    setPreferences(subscription.preferences);
    setErrors({});
    setHasChanges(false);
  }, [subscription]);

  // Check for changes
  useEffect(() => {
    const rmUserChanged = rmUserId !== subscription.rmUserId;
    const timezoneChanged = timezone !== subscription.timezone;
    const preferencesChanged =
      JSON.stringify(preferences) !== JSON.stringify(subscription.preferences);
    setHasChanges(rmUserChanged || timezoneChanged || preferencesChanged);
  }, [rmUserId, timezone, preferences, subscription]);

  // Validation using shared utility
  const validateForm = (): boolean => {
    const validationErrors = validateSubscriptionForm(
      '', // No brand code validation needed for preferences
      timezone,
      preferences,
      false, // Don't validate brand code
    );

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Handlers
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    await onSave(subscription.id, {
      rmUserId,
      timezone,
      preferences: preferencesToInput(preferences),
    });
  };

  const handlePreferenceChange = (
    key: keyof Types.ReportSubscriptionPreferences,
    value: boolean | string | string[] | number | null,
  ) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear errors when user makes changes
    if (
      key === 'includeMonday' ||
      key === 'includeTuesday' ||
      key === 'includeWednesday' ||
      key === 'includeThursday' ||
      key === 'includeFriday' ||
      key === 'includeSaturday' ||
      key === 'includeSunday'
    ) {
      clearFieldError(errors, setErrors, 'days');
    }
    if (key === 'deliveryTime') {
      clearFieldError(errors, setErrors, 'deliveryTime');
    }
    if (key === 'startDate' || key === 'endDate') {
      clearFieldError(errors, setErrors, 'dateRange');
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    clearFieldError(errors, setErrors, 'timezone');
  };

  const handleClose = () => {
    if (hasChanges && !loading) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?',
      );
      if (!confirmClose) return;
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <Card className='w-full max-w-5xl max-h-[90vh] overflow-y-auto'>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <div>
            <CardTitle className='text-lg'>Edit Preferences</CardTitle>
            <CardDescription>
              Update your automated report preferences for{' '}
              {subscription.hotel?.name || subscription.brandCode}
            </CardDescription>
          </div>
          <Button
            variant='ghost'
            size='sm'
            className='h-8 w-8 p-0'
            onClick={handleClose}
            disabled={loading}
          >
            <X className='h-4 w-4' />
          </Button>
        </CardHeader>

        <CardContent className='space-y-4'>
          {/* Hotel Info - Full Width */}
          <div className='bg-gray-50 rounded-lg p-3'>
            <div className='text-sm font-medium'>
              {subscription.hotel?.name || `Brand ${subscription.brandCode}`}
            </div>
            <div className='text-xs text-muted-foreground'>
              {subscription.hotel?.brand_code} • {subscription.brandCode}
            </div>
          </div>

          {/* Subscription Description - Full Width */}
          <div className='space-y-2'>
            <Label htmlFor='subscriptionDescription'>Subscription Description (Optional)</Label>
            <Input
              id='subscriptionDescription'
              value={preferences.description ?? ''}
              onChange={(e) => handlePreferenceChange('description', e.target.value || null)}
              placeholder='e.g. Weekend Reports, Vacation Sender'
              disabled={loading}
            />
            <p className='text-xs text-muted-foreground'>Used as a label in the UI</p>
          </div>

          {/* Top Row */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
            {/* Timezone */}
            <div className='space-y-2'>
              <Label htmlFor='timezone'>Timezone</Label>
              <Select value={timezone} onValueChange={handleTimezoneChange} disabled={loading}>
                <SelectTrigger className={errors.timezone ? 'border-red-500' : ''}>
                  <SelectValue placeholder='Select timezone' />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.timezone && <p className='text-sm text-red-500'>{errors.timezone}</p>}
            </div>

            {/* Revenue Manager */}
            <div className='space-y-2'>
              <Label htmlFor='rmUserId'>Revenue Manager</Label>
              <RmUserSelect value={rmUserId} onChange={setRmUserId} disabled={loading} />
            </div>

            {/* Delivery Time */}
            <div className='space-y-2'>
              <Label htmlFor='deliveryTime'>Delivery Time</Label>
              <Select
                value={preferences.deliveryTime}
                onValueChange={(value) => handlePreferenceChange('deliveryTime', value)}
                disabled={loading}
              >
                <SelectTrigger className={errors.deliveryTime ? 'border-red-500' : ''}>
                  <SelectValue placeholder='Select delivery time' />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_TIME_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.deliveryTime && <p className='text-sm text-red-500'>{errors.deliveryTime}</p>}
            </div>

            {/* Priority */}
            <div className='space-y-2'>
              <Label htmlFor='priority'>Priority</Label>
              <Select
                value={String(preferences.priority ?? 0)}
                onValueChange={(value) => handlePreferenceChange('priority', Number(value))}
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select priority' />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>Higher wins when overlapping</p>
            </div>
          </div>

          {/* Schedule Row */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Days */}
            <div className='space-y-3'>
              <Label>Days to Receive Reports</Label>
              <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2'>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='monday'
                    checked={preferences.includeMonday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeMonday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='monday' className='text-sm font-normal'>
                    Monday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='tuesday'
                    checked={preferences.includeTuesday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeTuesday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='tuesday' className='text-sm font-normal'>
                    Tuesday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='wednesday'
                    checked={preferences.includeWednesday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeWednesday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='wednesday' className='text-sm font-normal'>
                    Wednesday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='thursday'
                    checked={preferences.includeThursday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeThursday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='thursday' className='text-sm font-normal'>
                    Thursday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='friday'
                    checked={preferences.includeFriday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeFriday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='friday' className='text-sm font-normal'>
                    Friday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='saturday'
                    checked={preferences.includeSaturday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeSaturday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='saturday' className='text-sm font-normal'>
                    Saturday
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='sunday'
                    checked={preferences.includeSunday}
                    onCheckedChange={(checked) =>
                      handlePreferenceChange('includeSunday', !!checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor='sunday' className='text-sm font-normal'>
                    Sunday
                  </Label>
                </div>
              </div>
              {errors.days && <p className='text-sm text-red-500'>{errors.days}</p>}
            </div>

            {/* Date Range */}
            <div className='space-y-2'>
              <Label>Date Range (Optional)</Label>
              <div className='grid grid-cols-2 gap-2'>
                <div className='space-y-1'>
                  <Label htmlFor='startDate' className='text-xs text-muted-foreground'>
                    Start Date
                  </Label>
                  <Input
                    id='startDate'
                    type='date'
                    value={preferences.startDate ?? ''}
                    onChange={(e) =>
                      handlePreferenceChange('startDate', e.target.value ? e.target.value : null)
                    }
                    disabled={loading}
                  />
                </div>
                <div className='space-y-1'>
                  <Label htmlFor='endDate' className='text-xs text-muted-foreground'>
                    End Date
                  </Label>
                  <Input
                    id='endDate'
                    type='date'
                    value={preferences.endDate ?? ''}
                    onChange={(e) =>
                      handlePreferenceChange('endDate', e.target.value ? e.target.value : null)
                    }
                    disabled={loading}
                  />
                </div>
              </div>
              {errors.dateRange && <p className='text-sm text-red-500'>{errors.dateRange}</p>}
            </div>
          </div>

          {/* AI Notes */}
          <div className='space-y-2'>
            <Label>AI Notes</Label>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='useAiNotes'
                checked={preferences.useAiNotes}
                onCheckedChange={(checked) => handlePreferenceChange('useAiNotes', !!checked)}
                disabled={loading}
              />
              <Label htmlFor='useAiNotes' className='text-sm font-normal'>
                Include AI-generated notes
              </Label>
            </div>
          </div>

          {/* Recipients - Full Width */}
          <div className='space-y-2'>
            <Label>Recipients (Optional)</Label>
            <HotelTeamRecipientsSelect
              brandCode={subscription.brandCode}
              value={preferences.recipientTeamMemberIds}
              onChange={(ids) => handlePreferenceChange('recipientTeamMemberIds', ids)}
              disabled={loading}
            />
            <p className='text-xs text-muted-foreground'>Select existing Hotel Team members</p>
          </div>

          {/* Message - Full Width */}
          <div className='space-y-2'>
            <Label htmlFor='message'>Message (Optional)</Label>
            <Textarea
              id='message'
              value={preferences.message ?? ''}
              onChange={(e) => handlePreferenceChange('message', e.target.value || null)}
              placeholder='Add a note to include with the report email'
              disabled={loading}
            />
          </div>

          {/* No Active Team Members Warning */}
          {hasNoActiveTeamMembers && (
            <Alert variant='destructive'>
              <AlertTriangle className='h-4 w-4' />
              <AlertDescription>
                There are no active hotel team members for this brand code. At least one active team
                member is required to save an automated subscription.
              </AlertDescription>
            </Alert>
          )}

          {/* Form Actions */}
          <div className='flex justify-end space-x-2 pt-4'>
            <Button type='button' variant='outline' onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !hasChanges || hasNoActiveTeamMembers}
            >
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
